import { createContext, useState, useEffect } from 'react';
import { GroupedCashFlows, TransactionCategory } from '@algoan/dashboard/model';
import { isCashFlowCollection } from '@algoan/dashboard/utils';
import { getEveryCashFlowsFromAnalysis } from '@algoan/dashboard/calculators';

interface ICashFlowCollectionExpandedContext {
  isExpanded: (category: TransactionCategory) => boolean;
  toggleExpansion: (category: TransactionCategory) => void;
}

export const CashFlowCollectionExpandedContext = createContext<ICashFlowCollectionExpandedContext>({
  isExpanded: () => false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  toggleExpansion: () => {},
});

export const useCashFlowCollectionExpandedState = (
  groupedCashFlows?: GroupedCashFlows,
): {
  isEveryCashFlowCollectionExpanded: () => boolean;
  isExpanded: ICashFlowCollectionExpandedContext['isExpanded'];
  toggleEveryCollectionExpansion: () => void;
  toggleExpansion: ICashFlowCollectionExpandedContext['toggleExpansion'];
} => {
  const [expandedStates, setExpandedState] = useState<Partial<Record<TransactionCategory, boolean>>>({});

  const setAllExpandedState = (expandedState: boolean): void =>
    setExpandedState(
      (Object.keys(expandedStates) as TransactionCategory[]).reduce<typeof expandedStates>(
        (expandedStates, category) => {
          expandedStates[category] = expandedState;

          return expandedStates;
        },
        {},
      ),
    );

  const isEveryCashFlowCollectionExpanded = (): boolean =>
    (Object.keys(expandedStates) as TransactionCategory[]).every((category) => expandedStates[category]);
  const isExpanded = (category: TransactionCategory): boolean => !!expandedStates[category];
  const toggleExpansion = (category: TransactionCategory): void =>
    setExpandedState({ ...expandedStates, [category]: !expandedStates[category] });
  const toggleEveryCollectionExpansion = (): void => {
    if (isEveryCashFlowCollectionExpanded()) {
      return setAllExpandedState(false);
    }

    setAllExpandedState(true);
  };

  useEffect(() => {
    if (groupedCashFlows === undefined) return;

    setExpandedState(
      getEveryCashFlowsFromAnalysis(groupedCashFlows)
        .filter(isCashFlowCollection)
        .reduce<Partial<Record<TransactionCategory, boolean>>>((accumulator, cashFlowCollection) => {
          accumulator[cashFlowCollection.category] = false;

          return accumulator;
        }, {}),
    );
  }, [setExpandedState, groupedCashFlows]);

  return { isEveryCashFlowCollectionExpanded, isExpanded, toggleEveryCollectionExpansion, toggleExpansion };
};
